import { useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { ErrorState, Loading, Problem } from '../components/chrome'
import { useI18n } from '../lib/i18n'
import { browserSupportsWebAuthn, passkeyErrorMessage } from '../lib/passkeys'
import { useSignOut, useStaffState } from '../lib/session'
import { STAFF_STATE_KEY } from '../lib/trpc'
import { useRegisterPasskey, useTouchPasskey } from '../lib/use-passkeys'

/*
 * The staff's sign-in, second step (ADR 0008 §1): after the email link, the passkey, with the
 * device's fingerprint, face or PIN; without it there is no session. Someone without a passkey
 * registers the first one here, only with the operator tool's permission, and then signs in with
 * it; without the permission, the page says how to get one. Not in the Figma: marked for design
 * review.
 */
export function PasskeyPage() {
  const state = useStaffState()
  // Kept here: once registered, the state says «passkey», and the page goes on to use it.
  const [enrolled, setEnrolled] = useState(false)
  if (state.isPending) return <Loading />
  if (state.isError) return <ErrorState onRetry={() => void state.refetch()} />
  const s = state.data
  if (s.status === 'anonymous') return <Navigate to="/entrar" replace />
  if (s.status === 'signed-in') return <Navigate to="/" replace />
  return (
    <>
      {enrolled || s.next === 'passkey' ? (
        <TouchStep enrolled={enrolled} />
      ) : s.next === 'enroll' ? (
        <EnrollStep onEnrolled={() => setEnrolled(true)} />
      ) : (
        <EnrollmentRequired />
      )}
      <Leave />
    </>
  )
}

const supported = () => browserSupportsWebAuthn()

function TouchStep({ enrolled }: { enrolled: boolean }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const touch = useTouchPasskey()
  const heading = useRef<HTMLHeadingElement>(null)
  // After registering, the focus goes to what changed, so a screen reader says it.
  useEffect(() => {
    if (enrolled) heading.current?.focus()
  }, [enrolled])
  const canUse = supported()
  const run = () =>
    touch.mutate(undefined, {
      onSuccess: () => {
        void queryClient
          .invalidateQueries({ queryKey: STAFF_STATE_KEY })
          .then(() => navigate({ to: '/', replace: true }))
      },
    })
  return (
    <Card>
      <h1 ref={heading} tabIndex={-1} className="text-2xl outline-none">
        {enrolled ? t('enroll.done.title') : t('passkey.title')}
      </h1>
      <p className="mt-3 text-ink-muted">{enrolled ? t('enroll.done.body') : t('passkey.body')}</p>
      <Problem>
        {!canUse
          ? t('webauthn.unsupported')
          : touch.isError
            ? t(passkeyErrorMessage(touch.error))
            : null}
      </Problem>
      <Button
        size="lg"
        className="mt-6 w-full aria-disabled:cursor-progress aria-disabled:opacity-50"
        disabled={!canUse}
        // While the device is asked, the button says so and keeps the focus, for a retry.
        aria-disabled={touch.isPending || touch.isSuccess || undefined}
        onClick={() => {
          if (!touch.isPending && !touch.isSuccess) run()
        }}
      >
        {touch.isPending || touch.isSuccess
          ? t('passkey.busy')
          : enrolled
            ? t('enroll.done.submit')
            : t('passkey.submit')}
      </Button>
      {!enrolled && (
        <details className="mt-6 text-sm">
          <summary className="cursor-pointer text-ink-muted hover:text-ink">
            {t('passkey.lost.summary')}
          </summary>
          <p className="mt-2 text-ink-muted">{t('passkey.lost.body')}</p>
        </details>
      )}
    </Card>
  )
}

function EnrollStep({ onEnrolled }: { onEnrolled: () => void }) {
  const { t } = useI18n()
  const register = useRegisterPasskey()
  const canUse = supported()
  return (
    <Card>
      <h1 className="text-2xl">{t('enroll.title')}</h1>
      <p className="mt-3 text-ink-muted">{t('enroll.body')}</p>
      <p className="mt-3 text-sm text-ink-muted">{t('enroll.hint')}</p>
      <Problem>
        {!canUse
          ? t('webauthn.unsupported')
          : register.isError
            ? t(passkeyErrorMessage(register.error))
            : null}
      </Problem>
      <Button
        size="lg"
        className="mt-6 w-full aria-disabled:cursor-progress aria-disabled:opacity-50"
        disabled={!canUse}
        aria-disabled={register.isPending || undefined}
        onClick={() => {
          if (!register.isPending) register.mutate(undefined, { onSuccess: onEnrolled })
        }}
      >
        {register.isPending ? t('enroll.busy') : t('enroll.submit')}
      </Button>
    </Card>
  )
}

function EnrollmentRequired() {
  const { t } = useI18n()
  const state = useStaffState()
  const [checked, setChecked] = useState(false)
  return (
    <Card>
      <h1 className="text-2xl">{t('enrollmentRequired.title')}</h1>
      <p className="mt-3 text-ink-muted">{t('enrollmentRequired.body')}</p>
      <p role="status" className="text-sm not-empty:mt-4">
        {checked && !state.isFetching ? t('enrollmentRequired.stillMissing') : ''}
      </p>
      <Button
        size="lg"
        className="mt-6 w-full"
        disabled={state.isFetching}
        onClick={() => void state.refetch().then(() => setChecked(true))}
      >
        {t('enrollmentRequired.check')}
      </Button>
    </Card>
  )
}

/** Leaves the half-done sign-in: the session of the email link closes. */
function Leave() {
  const { t } = useI18n()
  const signOut = useSignOut()
  return (
    <div className="mt-4 text-center">
      <Button variant="ghost" size="sm" onClick={() => signOut.mutate()}>
        {t('common.signOut')}
      </Button>
    </div>
  )
}
